import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  makeStyles,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import LaunchIcon from '@material-ui/icons/Launch';
import { PageableTable, RouterNavLink } from 'components';
import { PageableTableHeader } from 'components/PageableTable/components';
import {
  getTableSettingsStorageKey,
  TableSettings,
  useDebounce,
  useDocumentTitle,
  useLocalStorageSettings,
} from 'hooks';
import {
  exportCompaniesList,
  fetchClients,
  fetchCompanyIndustries,
  fetchCompanyLeaders,
} from 'lib/Api/Clients';
import { stringAvatar } from 'lib/Helper/Avatars';
import {
  PagedClients,
  companyIndustries,
  companyLeaders,
} from 'lib/Model/Client';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  resolveMergedAliasesToPlatformNames,
  resolvePlatformName,
} from './SuggestedMatches';
import { getUserData } from 'lib/Helper/Auth';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      '& .MuiCardHeader-action': {
        marginRight: 0,
        marginTop: theme.spacing(2),
        alignSelf: 'stretch',
      },
    },
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      width: '100%',
      '& .MuiButton-root': {
        width: '100%',
      },
    },
  },
  cardTitle: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.5rem',
    },
  },
  cardSubheader: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.875rem',
    },
  },
  companyButton: {
    paddingLeft: 0,
  },
  companyCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  formControl: {
    marginBottom: theme.spacing(2),
  },
  smallAvatar: {
    marginRight: theme.spacing(0.5),
    width: 22,
    height: 22,
    fontSize: 12,
  },
  accountManager: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: theme.spacing(1),
  },
  othersLink: {
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    cursor: 'pointer',
  },
}));

export const ClientList: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const classes = useStyles();

  const [pageNumber, setPageNumber] = React.useState(0);
  const { pathname, hash, search } = useLocation();
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(
      getTableSettingsStorageKey(pathname, hash),
      { rowsPerPage: 25 },
    );
  const history = useHistory();

  useDocumentTitle('Clients');

  const searchParams = new URLSearchParams(search);
  const initialSearch = searchParams.get('search');
  const [textSearch, setTextSearch] = React.useState<string | undefined>(
    initialSearch || undefined,
  );

  const debouncedTextSearch = useDebounce(textSearch, 600);
  const [brand] = React.useState<string[]>([]);
  const [leader, setLeader] = React.useState<string[]>([]);
  const [churn] = React.useState<string[]>([]);
  const [headcount, setHeadcount] = React.useState<string[]>([]);
  const [industry, setIndustry] = React.useState<string[]>([]);
  const [hasOpenDeal] = React.useState<string | undefined>();

  const [industriesLoading, setIndustriesLoading] = React.useState(false);
  const [industriesData, setIndustriesData] = React.useState<
    companyIndustries[] | undefined
  >();

  const [leadersLoading, setLeadersLoading] = React.useState(false);
  const [leadersData, setLeadersData] = React.useState<
    companyLeaders[] | undefined
  >();

  const [exporting, setExporting] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');

  const [data, setData] = React.useState<PagedClients | undefined>();
  const [error, setError] = React.useState(false);
  const [industriesError, setIndustriesError] = React.useState(false);
  const [leadersError, setLeadersError] = React.useState(false);

  const abortControllerRef = React.useRef<AbortController | null>(null);
  const lastRequestTimeRef = React.useRef<number>(0);

  const fetchData = React.useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const requestTime = Date.now();
    lastRequestTimeRef.current = requestTime;

    setLoading(true);
    setError(false);
    fetchClients(
      pageNumber + 1,
      tableSettings.rowsPerPage,
      debouncedTextSearch,
      brand,
      churn,
      leader,
      headcount,
      industry,
      hasOpenDeal,
      abortControllerRef.current.signal,
    )
      .then((d) => {
        if (requestTime === lastRequestTimeRef.current) {
          setLoading(false);
          setData(d);
        }
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setLoading(false);
          setError(true);
        }
      });
  }, [
    setLoading,
    setData,
    setError,
    pageNumber,
    tableSettings.rowsPerPage,
    debouncedTextSearch,
    brand,
    churn,
    leader,
    headcount,
    industry,
    hasOpenDeal,
  ]);

  const exportData = React.useCallback(async () => {
    setExporting(true);
    setSnackbarMessage('Preparing your export…');
    setSnackbarOpen(true);
    try {
      await exportCompaniesList(
        debouncedTextSearch,
        brand,
        churn,
        leader,
        headcount,
        industry,
        hasOpenDeal,
      );
      setSnackbarMessage('Your export has downloaded.');
    } catch {
      setSnackbarMessage('Export failed. Please try again.');
    } finally {
      setExporting(false);
      setSnackbarOpen(true);
    }
  }, [
    debouncedTextSearch,
    brand,
    churn,
    leader,
    headcount,
    industry,
    hasOpenDeal,
  ]);

  const fetchIndustries = React.useCallback(() => {
    setIndustriesLoading(true);
    setIndustriesError(false);
    fetchCompanyIndustries()
      .then((d) => {
        setIndustriesData(d);
      })
      .catch(() => {
        setIndustriesError(true);
      })
      .finally(() => {
        setIndustriesLoading(false);
      });
  }, [setIndustriesLoading, setIndustriesData, setIndustriesError]);

  const fetchLeaders = React.useCallback(() => {
    setLeadersLoading(true);
    setLeadersError(false);
    fetchCompanyLeaders()
      .then((d) => {
        setLeadersData(d);
      })
      .catch(() => {
        setLeadersError(true);
      })
      .finally(() => {
        setLeadersLoading(false);
      });
  }, [setLeadersLoading, setLeadersData, setLeadersError]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    fetchIndustries();
    fetchLeaders();
  }, [fetchIndustries, fetchLeaders]);

  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const industryOptions = industriesData ?? [];

  const leaderOptions = [
    { company_leader: 'No CAM' },
    { company_leader: 'KonnektTalexio Administrator' },
    ...(leadersData ?? []),
  ];

  const columns: PageableTableHeader[] = [
    {
      key: 'id',
      label: '',
      sortable: false,
      props: {
        style: {
          width: 50,
        },
      },
    },
    {
      key: 'company',
      label: 'Company',
      sortable: false,
    },
    {
      key: 'cam',
      label: 'Client Account Manager',
      sortable: false,
    },
    {
      key: 'industry',
      label: 'Industry',
      sortable: false,
    },
    {
      key: 'headcount',
      label: 'Headcount',
      sortable: false,
    },
  ];

  const user = getUserData();
  if (!user) {
    return null;
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        classes={{
          root: classes.cardHeader,
          title: classes.cardTitle,
          subheader: classes.cardSubheader,
        }}
        title="KonnektTalexio Clients"
        subheader="Browse through a list of client profiles. If the company has many profile merges, you will find more in-depth information on that client."
        action={
          <div className={classes.headerActions}>
            <Button
              color="secondary"
              href="https://docs.google.com/document/d/1KI-fZ_E-io0DvPgdQ3tx8LPLRgfgo5Ojj5udRQtpKVs/edit#heading=h.mqyf0y3flxiq"
              target="_blank"
              rel="noopener noreferrer"
            >
              Help
            </Button>
            <Button
              color="primary"
              onClick={() => history.push('/dashboards#commercial')}
              startIcon={<LaunchIcon />}
            >
              Commercial Dashboard
            </Button>
            {user?.permissions?.includes('client_export') && (
              <Button
                color="secondary"
                onClick={exportData}
                disabled={exporting}
              >
                Export Data
                {exporting && (
                  <Box ml={1}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Button>
            )}
          </div>
        }
      />
      <CardContent style={{ paddingTop: 0, paddingBottom: 8 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              value={textSearch}
              variant="outlined"
              autoFocus
              onChange={(e) =>
                setTextSearch(e.currentTarget.value ?? undefined)
              }
              fullWidth
              placeholder="Type here to search for a company"
              inputProps={{ 'aria-label': 'Search companies' }}
            />
          </Grid>
          <Grid item xs={4} sm={4} lg={4}>
            <FormControl className={classes.formControl} fullWidth>
              <Autocomplete
                multiple
                loading={leadersLoading}
                noOptionsText={
                  leadersError ? 'Could not load CAMs' : 'No CAMs found'
                }
                getOptionLabel={(o) => `${o.company_leader}`}
                options={leaderOptions}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Filter by CAM"
                    placeholder={leader.length > 0 ? '' : 'All CAMs'}
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                value={leaderOptions.filter((r) =>
                  leader.includes(r.company_leader),
                )}
                onChange={(e, newValue) => {
                  setLeader(newValue.map((v) => v.company_leader));
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={4} sm={4} lg={4}>
            <FormControl className={classes.formControl} fullWidth>
              <Autocomplete
                multiple
                options={[
                  '0-9 employees',
                  '10-49 employees',
                  '50-249 employees',
                  '250+ employees',
                ]}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Filter by Headcount"
                    placeholder={headcount.length > 0 ? '' : 'All Sizes'}
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                value={headcount}
                onChange={(e, newValue) => {
                  setHeadcount(newValue);
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={4} sm={4} lg={4}>
            <FormControl className={classes.formControl} fullWidth>
              <Autocomplete
                multiple
                loading={industriesLoading}
                noOptionsText={
                  industriesError
                    ? 'Could not load industries'
                    : 'No industries found'
                }
                getOptionLabel={(o) => `${o.industry}`}
                options={industryOptions}
                renderInput={(params) => (
                  <TextField
                    variant="outlined"
                    label="Filter by Industry"
                    placeholder={industry.length > 0 ? '' : 'All Industries'}
                    {...params}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                value={industryOptions.filter((r) =>
                  industry.includes(r.industry),
                )}
                onChange={(e, newValue) => {
                  setIndustry(newValue.map((v) => v.industry));
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>

      {error ? (
        <CardContent>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchData}>
                Retry
              </Button>
            }
          >
            Could not load clients. Check your connection and try again.
          </Alert>
        </CardContent>
      ) : (
        <PageableTable
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          showSkeletonLoading
          rows={
            data?.data?.map((d) => ({
              key: d.id,
              cells: [
                {
                  key: 'id',
                  display: d.company_title && (
                    <Avatar
                      onClick={() => {
                        history.push({
                          pathname: `/clients/${d.id}`,
                          state: { search: textSearch },
                        });
                      }}
                      variant="rounded"
                      {...stringAvatar(d.company_title)}
                    />
                  ),
                },
                {
                  key: 'company',
                  display: (
                    <div className={classes.companyCell}>
                      <Button
                        className={classes.companyButton}
                        component={RouterNavLink}
                        variant="text"
                        to={{
                          pathname: `/clients/${d.id}`,
                          state: { search: textSearch },
                        }}
                        color="primary"
                      >
                        {d.company_title}
                      </Button>
                      <Typography variant="caption">
                        Sources:&nbsp;
                        {resolvePlatformName(d.id)}
                        {(() => {
                          const mergedAliases =
                            resolveMergedAliasesToPlatformNames(
                              d.merged_aliases ?? null,
                            );

                          if (mergedAliases.length === 0) {
                            return '';
                          }

                          const maxShow = 2;
                          const visibleAliases = mergedAliases.slice(
                            0,
                            maxShow,
                          );
                          const remaining =
                            mergedAliases.length - visibleAliases.length;

                          return (
                            <>
                              {', ' + visibleAliases.join(', ')}
                              {remaining > 0 && (
                                <>
                                  {' and '}
                                  <Tooltip
                                    title={
                                      <>
                                        {mergedAliases
                                          .slice(maxShow)
                                          .map((name, index) => (
                                            <div key={index}>{name}</div>
                                          ))}
                                      </>
                                    }
                                    placement="top"
                                  >
                                    <span className={classes.othersLink}>
                                      {`${remaining} ${
                                        remaining > 1 ? 'others' : 'other'
                                      }`}
                                    </span>
                                  </Tooltip>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </Typography>
                    </div>
                  ),
                },
                {
                  key: 'cam',
                  display: d.zendesk_leader ? (
                    <div className={classes.accountManager}>
                      <Avatar
                        className={classes.smallAvatar}
                        {...stringAvatar(d.zendesk_leader)}
                      />
                      <Typography variant="body1">
                        {d.zendesk_leader}
                      </Typography>
                    </div>
                  ) : null,
                },

                {
                  key: 'industry',
                  display: d.industry,
                },
                {
                  key: 'headcount',
                  display: d.headcount,
                },
              ],
            })) ?? []
          }
          loading={loading}
          pageNumber={pageNumber}
          rowCount={data?.total_count ?? 0}
          columns={columns}
          emptyTableText="No clients match these filters. Try clearing a filter or your search."
          onChangePage={setPageNumber}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Card>
  );
};
